// Extrait de ClinicAdminDashboard.js (handover bloc F3-A) — JSX inchangé ; période et données fournies par la page.
export default function ClinicPerformanceTab({ businessKpi, businessPeriod, onPeriodChange }) {
  return (
    <div className="space-y-6">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Performance du suivi
        </h2>
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
          {[["7d","7 jours"],["30d","30 jours"],["90d","90 jours"]].map(([val,label]) => (
            <button key={val} onClick={() => onPeriodChange(val)} data-testid={`period-${val}`}
              className={`px-3.5 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 ${businessPeriod === val ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {!businessKpi ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-6 h-6 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Row 1 — Headline KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl border border-slate-200/60 p-5 space-y-1" data-testid="kpi-suivi">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Taux de suivi actif</p>
              <p className="text-3xl font-bold text-emerald-600">{businessKpi.suiviActif.rate}%</p>
              <p className="text-xs text-slate-500">{businessKpi.suiviActif.patientsAvecCheckin} / {businessKpi.suiviActif.totalActifs} patients</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200/60 p-5 space-y-1" data-testid="kpi-retention">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Retention patient</p>
              <p className="text-3xl font-bold text-sky-600">{businessKpi.retention.rate}%</p>
              <p className="text-xs text-slate-500">{businessKpi.retention.patientsPerdus} perdu{businessKpi.retention.patientsPerdus > 1 ? "s" : ""} sur la periode</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200/60 p-5 space-y-1" data-testid="kpi-delai">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Delai prise en charge</p>
              <p className="text-3xl font-bold text-amber-600">{businessKpi.delaiPriseEnCharge.moyenneMinutes != null ? `${businessKpi.delaiPriseEnCharge.moyenneMinutes} min` : "—"}</p>
              <p className="text-xs text-slate-500">Temps moyen alerte → IDE</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200/60 p-5 space-y-1" data-testid="kpi-recuperes">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Patients recuperes</p>
              <p className="text-3xl font-bold text-violet-600">{businessKpi.patientsRecuperes.count}</p>
              <p className="text-xs text-slate-500">Recontactes apres decrochage</p>
            </div>
          </div>

          {/* Row 2 — Alertes + Interactions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-slate-200/60 p-5" data-testid="kpi-alertes">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Alertes interceptees</p>
              <div className="flex items-end gap-6">
                <div>
                  <p className="text-3xl font-bold text-rose-600">{businessKpi.alertesInterceptees.total}</p>
                  <p className="text-xs text-slate-500 mt-0.5">situations detectees</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-emerald-600">{businessKpi.alertesInterceptees.tauxResolutionIde}%</p>
                  <p className="text-xs text-slate-500 mt-0.5">gerees par l'IDE seule</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200/60 p-5" data-testid="kpi-interactions">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Volume d'interactions</p>
              <p className="text-3xl font-bold text-slate-800">{businessKpi.interactions.total.toLocaleString("fr-FR")}</p>
              <div className="flex gap-4 mt-2">
                <span className="text-xs text-slate-500"><span className="font-semibold text-emerald-600">{businessKpi.interactions.checkins}</span> check-ins</span>
                <span className="text-xs text-slate-500"><span className="font-semibold text-sky-600">{businessKpi.interactions.messages}</span> messages</span>
                <span className="text-xs text-slate-500"><span className="font-semibold text-amber-600">{businessKpi.interactions.transmissions}</span> transmissions</span>
              </div>
            </div>
          </div>

          {/* Row 3 — Actes generes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-slate-200/60 p-5" data-testid="kpi-rdv">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Demandes de RDV generees</p>
              <div className="flex items-end gap-6">
                <div>
                  <p className="text-3xl font-bold text-emerald-600">{businessKpi.demandesRdv.total}</p>
                  <p className="text-xs text-slate-500 mt-0.5">demandes creees</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-sky-600">{businessKpi.demandesRdv.traitees}</p>
                  <p className="text-xs text-slate-500 mt-0.5">traitees</p>
                </div>
              </div>
              {Object.keys(businessKpi.demandesRdv.parType).length > 0 && (
                <div className="flex gap-3 mt-3 pt-3 border-t border-slate-100">
                  {Object.entries(businessKpi.demandesRdv.parType).map(([type, count]) => (
                    <span key={type} className="text-xs text-slate-500">
                      <span className="font-semibold text-slate-700">{count}</span> {type === "consultation" ? "consultations" : type === "imagerie" ? "imageries" : type}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-white rounded-2xl border border-slate-200/60 p-5" data-testid="kpi-ordonnances">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Ordonnances emises</p>
              <p className="text-3xl font-bold text-slate-800">{businessKpi.ordonnances.total}</p>
              <div className="flex gap-4 mt-2">
                <span className="text-xs text-slate-500"><span className="font-semibold text-emerald-600">{businessKpi.ordonnances.parType.ordonnance_medicament || 0}</span> medicaments</span>
                <span className="text-xs text-slate-500"><span className="font-semibold text-sky-600">{businessKpi.ordonnances.parType.ordonnance_analyses || 0}</span> analyses</span>
                <span className="text-xs text-slate-500"><span className="font-semibold text-amber-600">{businessKpi.ordonnances.parType.ordonnance_imagerie || 0}</span> imageries</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
